// ============================================================================
// IMAGES TAB - Dedicated image management for session sidebar
// Table view with capture, upload, tags, avatar analysis, second screen push
// ============================================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Camera,
  Upload,
  Trash2,
  Monitor,
  MessageSquarePlus,
  X,
  Check,
  Pencil,
  ZoomIn,
  Tag,
  BookOpen,
  Info,
  Share2,
  Paintbrush,
  Loader2,
  Download,
  Eye,
  EyeOff,
  Sparkles,
  ImageIcon,
  Bookmark,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { GenerateImageModal } from './GenerateImageModal';
import { ConfirmModal } from './common/ConfirmModal';
import type { GenerateImageParams } from './GenerateImageModal';
import { getImageTaskStatus } from '../services/imageGen';
import type { DeviceInfo } from '../services/n8n';
import type { SessionAvatar, PinnedContentItem } from '../types/second-screen.types';
import {
  DeviceImageMetadata,
  ImageAnnotation,
  getDeviceImages,
  uploadDeviceImage,
  deleteDeviceImage,
  requestDeviceCapture,
  addImageToContext,
  addImageToGraph,
  updateImageTags,
  updateImageAnnotations,
  askAvatarAboutImage,
  bulkImageAction,
  setImageTaggedForExport,
} from '../services/deviceImages';
import { api } from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

interface ImagesTabProps {
  sessionId: string;
  currentPhase: string;
  sessionDevices: DeviceInfo[];
  onPinToSecondScreen: (item: PinnedContentItem) => void;
  sessionAvatars: SessionAvatar[];
  onStartOpenDesignTask?: (prompt: string) => Promise<void>;
  onGenerateBook?: () => void;
  hasWorkshopBookFeature?: boolean;
  onGenerateImage?: (params: GenerateImageParams) => Promise<string>;
}

type SortField = 'date' | 'source' | 'tag';
type SortDir = 'asc' | 'desc';

// ============================================================================
// INLINE TAG EDITOR
// ============================================================================

const InlineTagEditor: React.FC<{
  tags: string[];
  onUpdate: (tags: string[]) => void;
}> = ({ tags, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const addTag = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed) && tags.length < 10) {
      onUpdate([...tags, trimmed]);
      setInput('');
    }
  };

  const removeTag = (tag: string) => {
    onUpdate(tags.filter(t => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Escape') {
      setEditing(false);
      setInput('');
    }
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1 flex-wrap cursor-pointer group" onClick={() => setEditing(true)}>
        {tags.length > 0 ? (
          tags.map(tag => (
            <span key={tag} className="bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 rounded font-medium">
              {tag}
            </span>
          ))
        ) : (
          <span className="text-slate-400 text-xs opacity-0 group-hover:opacity-100 transition-opacity">+ tag</span>
        )}
        <Pencil size={10} className="text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity ml-1" />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {tags.map(tag => (
        <span key={tag} className="bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5">
          {tag}
          <X size={8} className="cursor-pointer hover:text-red-500" onClick={() => removeTag(tag)} />
        </span>
      ))}
      <input
        ref={inputRef}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => { addTag(); setEditing(false); }}
        className="w-16 text-[10px] bg-transparent border-b border-primary/30 outline-none text-slate-700 px-0.5"
        placeholder="add..."
        maxLength={50}
      />
    </div>
  );
};

// ============================================================================
// ASK AVATAR MODAL
// ============================================================================

const AskAvatarModal: React.FC<{
  image: DeviceImageMetadata;
  avatars: SessionAvatar[];
  onSubmit: (question: string, targetRoles?: string[]) => Promise<void>;
  onClose: () => void;
}> = ({ image, avatars, onSubmit, onClose }) => {
  const [question, setQuestion] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [sending, setSending] = useState(false);

  const handleSubmit = async () => {
    if (!question.trim()) return;
    setSending(true);
    await onSubmit(question.trim(), selectedRoles.length > 0 ? selectedRoles : undefined);
    setSending(false);
    onClose();
  };

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy">Ask Avatar About Image</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          {/* Image preview */}
          <div className="flex gap-4">
            <img
              src={image.presignedUrl}
              alt="Captured"
              className="w-24 h-16 object-cover rounded-lg border border-slate-200"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-navy truncate">
                {image.captureLabel || image.deviceId}
              </p>
              <p className="text-xs text-slate-500">{new Date(image.timestamp).toLocaleString()}</p>
              {image.extractedText && (
                <p className="text-xs text-slate-400 mt-1 line-clamp-2">{image.extractedText}</p>
              )}
            </div>
          </div>

          {/* Avatar selection */}
          {avatars.length > 0 && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Ask specific avatars (optional)</label>
              <div className="flex flex-wrap gap-2">
                {avatars.map(av => (
                  <button
                    key={av.role}
                    onClick={() => toggleRole(av.role)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      selectedRoles.includes(av.role)
                        ? 'bg-primary/10 border-primary text-primary font-medium'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {av.emoji && <span className="mr-1">{av.emoji}</span>}
                    {av.name || av.role}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Question input */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Your question</label>
            <textarea
              value={question}
              onChange={e => setQuestion(e.target.value)}
              placeholder="What can you tell me about this image?"
              className="w-full h-24 px-3 py-2 text-sm border border-slate-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              autoFocus
            />
          </div>

          <p className="text-xs text-slate-400">
            The avatar will see the image and extracted text. Response will appear in the Contributions tab.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={!question.trim() || sending}
            className="px-4 py-2 text-sm bg-primary text-white rounded-xl font-bold hover:brightness-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending...' : 'Ask Avatar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ANNOTATION OVERLAY
// ============================================================================

const AnnotationOverlay: React.FC<{
  imageUrl: string;
  annotations: ImageAnnotation[];
  onSave: (annotations: ImageAnnotation[]) => void;
  onClose: () => void;
}> = ({ imageUrl, annotations: initial, onSave, onClose }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [tool, setTool] = useState<'circle' | 'arrow' | 'text' | 'rectangle'>('rectangle');
  const [color, setColor] = useState('#ef4444');
  const [annots, setAnnots] = useState<ImageAnnotation[]>(initial);
  const [drawing, setDrawing] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(null);
  const [textInput, setTextInput] = useState('');
  const [textPos, setTextPos] = useState<{ x: number; y: number } | null>(null);

  const getRelativePos = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    };
  };

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // Wait for image to be fully decoded before drawing
    if (!img.complete || img.naturalWidth === 0) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    // Draw annotations
    for (const a of annots) {
      ctx.strokeStyle = a.color;
      ctx.fillStyle = a.color;
      ctx.lineWidth = 3;
      const ax = (a.x / 100) * canvas.width;
      const ay = (a.y / 100) * canvas.height;
      if (a.type === 'rectangle' && a.width !== undefined && a.height !== undefined) {
        ctx.strokeRect(ax, ay, (a.width / 100) * canvas.width, (a.height / 100) * canvas.height);
      } else if (a.type === 'circle' && a.width !== undefined) {
        ctx.beginPath();
        ctx.arc(ax, ay, (a.width / 100) * canvas.width, 0, Math.PI * 2);
        ctx.stroke();
      } else if (a.type === 'arrow' && a.endX !== undefined && a.endY !== undefined) {
        const ex = (a.endX / 100) * canvas.width;
        const ey = (a.endY / 100) * canvas.height;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        // Arrowhead
        const angle = Math.atan2(ey - ay, ex - ax);
        ctx.beginPath();
        ctx.moveTo(ex, ey);
        ctx.lineTo(ex - 15 * Math.cos(angle - 0.4), ey - 15 * Math.sin(angle - 0.4));
        ctx.moveTo(ex, ey);
        ctx.lineTo(ex - 15 * Math.cos(angle + 0.4), ey - 15 * Math.sin(angle + 0.4));
        ctx.stroke();
      } else if (a.type === 'text' && a.text) {
        ctx.font = `${Math.max(16, canvas.width / 40)}px sans-serif`;
        ctx.fillText(a.text, ax, ay);
      }
    }
  }, [annots]);

  useEffect(() => {
    redraw();
  }, [annots, redraw]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (tool === 'text') {
      setTextPos(getRelativePos(e));
      return;
    }
    setDrawing(true);
    setStartPos(getRelativePos(e));
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawing || !startPos) return;
    setDrawing(false);
    const end = getRelativePos(e);
    let newAnnot: ImageAnnotation;
    if (tool === 'rectangle') {
      newAnnot = { type: 'rectangle', x: startPos.x, y: startPos.y, width: end.x - startPos.x, height: end.y - startPos.y, color };
    } else if (tool === 'circle') {
      const radius = Math.sqrt((end.x - startPos.x) ** 2 + (end.y - startPos.y) ** 2);
      newAnnot = { type: 'circle', x: startPos.x, y: startPos.y, width: radius, color };
    } else {
      newAnnot = { type: 'arrow', x: startPos.x, y: startPos.y, endX: end.x, endY: end.y, color };
    }
    setAnnots(prev => [...prev, newAnnot]);
    setStartPos(null);
  };

  const handleTextSubmit = () => {
    if (textPos && textInput.trim()) {
      setAnnots(prev => [...prev, { type: 'text', x: textPos.x, y: textPos.y, text: textInput.trim(), color }]);
      setTextInput('');
      setTextPos(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-[90vw] w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-bold text-navy">Annotate Image</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        {/* Toolbar */}
        <div className="px-6 py-2 border-b border-slate-100 flex items-center gap-3">
          {(['rectangle', 'circle', 'arrow', 'text'] as const).map(t => (
            <button
              key={t}
              onClick={() => setTool(t)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium capitalize ${tool === t ? 'bg-primary text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              {t}
            </button>
          ))}
          <div className="w-px h-5 bg-slate-200 mx-1" />
          {['#ef4444', '#3b82f6', '#22c55e', '#eab308', '#ffffff'].map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-5 h-5 rounded-full border-2 ${color === c ? 'border-slate-700 scale-110' : 'border-slate-300'}`}
              style={{ backgroundColor: c }}
            />
          ))}
          <div className="flex-1" />
          <button
            onClick={() => setAnnots(prev => prev.slice(0, -1))}
            className="text-xs text-slate-500 hover:text-slate-700"
            disabled={annots.length === 0}
          >
            Undo
          </button>
          <button
            onClick={() => setAnnots([])}
            className="text-xs text-red-500 hover:text-red-700"
          >
            Clear All
          </button>
        </div>
        {/* Canvas */}
        <div className="relative p-4">
          <img ref={imgRef} src={imageUrl} alt="" className="hidden" onLoad={() => { redraw(); }} />
          <canvas
            ref={canvasRef}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            className="w-full rounded-lg cursor-crosshair border border-slate-200"
            style={{ maxHeight: '70vh' }}
          />
          {textPos && (
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-3 rounded-lg shadow-lg border">
              <input
                value={textInput}
                onChange={e => setTextInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleTextSubmit()}
                placeholder="Type annotation text..."
                className="text-sm border border-slate-200 rounded px-2 py-1 w-48"
                autoFocus
              />
              <div className="flex gap-2 mt-2">
                <button onClick={handleTextSubmit} className="text-xs text-primary font-medium">Add</button>
                <button onClick={() => { setTextPos(null); setTextInput(''); }} className="text-xs text-slate-500">Cancel</button>
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
          <button
            onClick={() => { onSave(annots); onClose(); }}
            className="px-4 py-2 text-sm bg-primary text-white rounded-xl font-bold hover:brightness-105 transition-all"
          >
            Save Annotations
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// EXPANDED IMAGE DETAIL
// ============================================================================

const ExpandedImageView: React.FC<{
  image: DeviceImageMetadata;
  onClose: () => void;
  onAnnotate: () => void;
  onPushToScreen: () => void;
  onAskAvatar: () => void;
  onAddToContext: () => void;
  onAddToGraph: () => void;
  onDelete: () => void;
  onDownload: () => void;
  onToggleExportTag: () => void;
  onStartOpenDesignTask?: (prompt: string) => Promise<void>;
}> = ({ image, onClose, onAnnotate, onPushToScreen, onAskAvatar, onAddToContext, onAddToGraph, onDelete, onDownload, onToggleExportTag, onStartOpenDesignTask }) => {
  const [generatingDesign, setGeneratingDesign] = useState(false);
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-[90vw] w-full mx-4 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-navy">
              {image.captureLabel || image.deviceId}
            </h3>
            <p className="text-xs text-slate-500">{new Date(image.timestamp).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-6 flex gap-6">
          <div className="flex-1 min-w-0">
            <img
              src={image.presignedUrl}
              alt={image.captureLabel || 'Captured image'}
              className="w-full rounded-xl border border-slate-200"
            />
            <div className="flex gap-2 mt-4 flex-wrap">
              <button onClick={onAnnotate} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 bg-white hover:bg-slate-50 rounded-xl text-slate-700 font-bold transition-colors">
                <Pencil size={12} /> Annotate
              </button>
              <button onClick={onPushToScreen} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 bg-white hover:bg-slate-50 rounded-xl text-slate-700 font-bold transition-colors">
                <Monitor size={12} /> Push to Screen
              </button>
              <button onClick={onAskAvatar} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 rounded-xl text-primary font-bold transition-colors">
                <MessageSquarePlus size={12} /> Ask Avatar
              </button>
              {onStartOpenDesignTask && image.extractedText && (
                <button
                  onClick={async () => {
                    setGeneratingDesign(true);
                    try {
                      await onStartOpenDesignTask(`Whiteboard sketch UI mockup:\n\n${image.extractedText}${image.tags?.length ? '\n\nContext tags: ' + image.tags.join(', ') : ''}`);
                    } finally {
                      setGeneratingDesign(false);
                      onClose();
                    }
                  }}
                  disabled={generatingDesign}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded-xl text-indigo-600 font-bold transition-colors disabled:opacity-50"
                >
                  {generatingDesign ? <Loader2 size={12} className="animate-spin" /> : <Paintbrush size={12} />} Open Design
                </button>
              )}
              {!image.addedToContext && image.extractedText && (
                <button onClick={onAddToContext} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 bg-white hover:bg-slate-50 rounded-xl text-slate-700 font-bold transition-colors">
                  <BookOpen size={12} /> Add to Context
                </button>
              )}
              {!image.addedToGraph && image.extractedText && (
                <button onClick={onAddToGraph} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 bg-white hover:bg-slate-50 rounded-xl text-slate-700 font-bold transition-colors">
                  <Share2 size={12} /> Add to Graph
                </button>
              )}
              <button
                onClick={onToggleExportTag}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-xl font-bold transition-colors ${
                  image.taggedForExport
                    ? 'border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20'
                    : 'border border-slate-200 bg-white hover:bg-slate-50 text-slate-700'
                }`}
              >
                <Bookmark size={12} /> {image.taggedForExport ? 'Tagged for Export' : 'Tag for Export'}
              </button>
              <button onClick={onDownload} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-xl text-blue-600 font-bold transition-colors">
                <Download size={12} /> Download
              </button>
              <button onClick={onDelete} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-red-50 hover:bg-red-100 rounded-xl text-red-600 font-bold transition-colors ml-auto">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
          {image.extractedText && (
            <div className="w-64 shrink-0">
              <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2">Extracted Text</h4>
              <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-700 whitespace-pre-wrap max-h-96 overflow-y-auto border border-slate-200">
                {image.extractedText}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// BULK TAG MODAL
// ============================================================================

const BulkTagModal: React.FC<{
  count: number;
  onApply: (tags: string[]) => void;
  onClose: () => void;
}> = ({ count, onApply, onClose }) => {
  const [input, setInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  const addTag = () => {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) {
      setTags(prev => [...prev, trimmed]);
      setInput('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg w-80 mx-4" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-bold text-navy">Add Tags to {count} Images</h3>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-1">
            {tags.map(tag => {
              const removeTag = () => setTags(prev => prev.filter(t => t !== tag));
              return (
                <span key={tag} className="bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  {tag} <X size={10} className="cursor-pointer" onClick={removeTag} />
                </span>
              );
            })}
          </div>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
            placeholder="Type tag and press Enter..."
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
            autoFocus
          />
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-slate-600">Cancel</button>
          <button
            onClick={() => { onApply(tags); onClose(); }}
            disabled={tags.length === 0}
            className="px-3 py-1.5 text-xs bg-primary text-white rounded-xl font-bold hover:brightness-105 transition-all disabled:opacity-50"
          >
            Apply Tags
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// WORKSHOP BOOK INFO BUTTON
// ============================================================================

const WorkshopBookInfoButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-8 h-8 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 hover:text-teal-600 hover:border-teal-300 transition-colors"
        title="Image size guidance"
      >
        <Info size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-50 w-64 bg-white border border-slate-200 rounded-xl shadow-lg p-4 text-xs text-slate-700">
          <div className="font-semibold text-slate-900 mb-2.5">Recommended image sizes</div>
          <div className="space-y-2.5">
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-8 h-12 bg-slate-100 border border-slate-200 rounded flex items-center justify-center text-slate-400 text-[9px] font-bold">2:3</div>
              <div>
                <div className="font-medium text-slate-800">Cover image</div>
                <div className="text-slate-500 mt-0.5">Portrait 2:3 — tall/vertical photo (e.g. 1000×1500px)</div>
              </div>
            </div>
            <div className="flex gap-3 items-start">
              <div className="flex-shrink-0 w-12 h-8 bg-slate-100 border border-slate-200 rounded flex items-center justify-center text-slate-400 text-[9px] font-bold">3:2</div>
              <div>
                <div className="font-medium text-slate-800">All other pages</div>
                <div className="text-slate-500 mt-0.5">Landscape 3:2 — wide/horizontal photo (e.g. 1500×1000px)</div>
              </div>
            </div>
          </div>
          <div className="mt-3 pt-2.5 border-t border-slate-100 text-slate-400">
            Portrait photos on landscape pages will be cropped significantly.
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TABLE CELL SUB-COMPONENTS
// ============================================================================

const ImageSourceCell: React.FC<{ image: DeviceImageMetadata; sessionDevices: DeviceInfo[] }> = ({ image, sessionDevices }) => {
  let sourceLabel: React.ReactNode;
  if (image.captureSource === 'ai-generated') {
    sourceLabel = <><Sparkles size={13} className="text-orange-500 shrink-0" />AI Generated</>;
  } else if (image.captureSource === 'manual') {
    sourceLabel = 'Manual Upload';
  } else {
    sourceLabel = sessionDevices.find(d => d.deviceId === image.deviceId)?.displayName || image.deviceId;
  }

  return (
    <td className="py-3 px-3">
      <span className="text-navy font-medium text-sm flex items-center gap-1.5">
        {sourceLabel}
      </span>
      {image.captureSource === 'ai-generated' && image.extractionStatus === 'pending' && (
        <span className="flex items-center gap-1 text-xs text-orange-500 mt-0.5">
          <Loader2 size={11} className="animate-spin" />Generating…
        </span>
      )}
      {image.captureLabel && image.captureSource !== 'ai-generated' && (
        <span className="block text-xs text-slate-400 mt-0.5">{image.captureLabel}</span>
      )}
      {image.captureSource === 'ai-generated' && image.captureLabel && (
        <span className="block text-xs text-slate-400 mt-0.5">{image.captureLabel}</span>
      )}
    </td>
  );
};

const ImageStatusCell: React.FC<{ image: DeviceImageMetadata }> = ({ image }) => (
  <td className="py-3 px-3">
    <div className="flex items-center gap-1.5 flex-wrap">
      {image.extractionStatus === 'complete' && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200"><Check size={8} /> OCR</span>
      )}
      {image.extractionStatus === 'pending' && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">Processing</span>
      )}
      {image.extractionStatus === 'failed' && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200">Failed</span>
      )}
      {image.addedToContext && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">In Context</span>
      )}
      {image.addedToGraph && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200">In Graph</span>
      )}
      {image.taggedForExport && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30"><Bookmark size={8} /> Export</span>
      )}
      {image.annotations && image.annotations.length > 0 && (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-600 border border-purple-200">Annotated</span>
      )}
    </div>
  </td>
);

function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return <ChevronUp size={12} className="opacity-20" />;
  return dir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ImagesTab: React.FC<ImagesTabProps> = ({
  sessionId,
  currentPhase,
  sessionDevices,
  onPinToSecondScreen,
  sessionAvatars,
  onStartOpenDesignTask,
  onGenerateBook,
  hasWorkshopBookFeature,
  onGenerateImage,
}) => {
  // Image state
  const [images, setImages] = useState<DeviceImageMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Participant publish state (s3Key → published)
  const [publishedAssets, setPublishedAssets] = useState<Set<string>>(new Set());
  const [publishingAsset, setPublishingAsset] = useState<string | null>(null);

  // Selection
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set());

  // Filter & sort
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Capture cooldowns
  const [captureCooldowns, setCaptureCooldowns] = useState<Record<string, number>>({});
  const cooldownTimersRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Upload
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadLabel, setUploadLabel] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Modals
  const [expandedImage, setExpandedImage] = useState<DeviceImageMetadata | null>(null);
  const [askAvatarImage, setAskAvatarImage] = useState<DeviceImageMetadata | null>(null);
  const [annotatingImage, setAnnotatingImage] = useState<DeviceImageMetadata | null>(null);
  const [showBulkTag, setShowBulkTag] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single'; imageId: string } | { type: 'bulk' } | null>(null);

  // Toast
  const [toast, setToast] = useState<string | null>(null);

  // Image generation
  const [showImageGenModal, setShowImageGenModal] = useState(false);
  const [imageGenLoading, setImageGenLoading] = useState(false);
  const [imageGenCountdown, setImageGenCountdown] = useState(0);
  const [sourceImage, setSourceImage] = useState<DeviceImageMetadata | null>(null);
  const imageGenCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCountdown = () => {
    setImageGenCountdown(0);
    if (imageGenCountdownRef.current) clearInterval(imageGenCountdownRef.current);
    imageGenCountdownRef.current = setInterval(() => setImageGenCountdown(p => p + 1), 1000);
  };
  const stopCountdown = () => {
    if (imageGenCountdownRef.current) { clearInterval(imageGenCountdownRef.current); imageGenCountdownRef.current = null; }
    setImageGenCountdown(0);
  };

  const handleGenerateImage = async (params: GenerateImageParams) => {
    if (!onGenerateImage) return;
    setImageGenLoading(true);
    startCountdown();
    const paramsWithSource: GenerateImageParams = {
      ...params,
      ...(sourceImage ? { sourceImageS3Key: sourceImage.s3Key } : {}),
    };
    try {
      const imageId = await onGenerateImage(paramsWithSource);
      setShowImageGenModal(false);
      setSourceImage(null);
      // Poll until complete then refresh
      const maxAttempts = 120;
      let attempts = 0;
      const poll = async (): Promise<void> => {
        attempts++;
        try {
          const result = await getImageTaskStatus(sessionId, imageId);
          if (result.status === 'complete' || result.status === 'failed') { fetchImages(); return; }
        } catch { /* continue */ }
        if (attempts < maxAttempts) { await new Promise<void>(r => setTimeout(r, 2000)); return poll(); }
        fetchImages();
      };
      setTimeout(() => poll(), 2000);
    } finally {
      setImageGenLoading(false);
      stopCountdown();
    }
  };

  // Camera devices
  const cameraDevices = sessionDevices.filter(d =>
    d.capabilities?.includes('camera') || d.assignedRole === 'camera'
  );

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const fetchImages = useCallback(async () => {
    try {
      const data = await getDeviceImages(sessionId);
      setImages(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch images:', err);
      setError('Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchImages();
    const interval = setInterval(fetchImages, 10000);
    return () => clearInterval(interval);
  }, [fetchImages]);

  // ============================================================================
  // CAPTURE
  // ============================================================================

  const startCooldown = (deviceId: string) => {
    setCaptureCooldowns(prev => ({ ...prev, [deviceId]: 30 }));
    if (cooldownTimersRef.current[deviceId]) clearInterval(cooldownTimersRef.current[deviceId]);
    cooldownTimersRef.current[deviceId] = setInterval(() => {
      setCaptureCooldowns(prev => {
        const remaining = (prev[deviceId] || 0) - 1;
        if (remaining <= 0) {
          clearInterval(cooldownTimersRef.current[deviceId]);
          const { [deviceId]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [deviceId]: remaining };
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      Object.values(cooldownTimersRef.current).forEach(clearInterval);
    };
  }, []);

  const handleCapture = async (deviceId: string) => {
    try {
      await requestDeviceCapture(deviceId, sessionId);
      startCooldown(deviceId);
      showToast('Capture requested');
      setTimeout(fetchImages, 3000);
    } catch (err: any) {
      setError('Capture failed: ' + err.message);
    }
  };

  // ============================================================================
  // UPLOAD
  // ============================================================================

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result as string;
        await uploadDeviceImage(sessionId, base64, undefined, currentPhase, uploadLabel || undefined);
        setUploadLabel('');
        setShowUpload(false);
        showToast('Image uploaded');
        setTimeout(fetchImages, 2000);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setError('Upload failed: ' + err.message);
      setUploading(false);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ============================================================================
  // ACTIONS
  // ============================================================================

  const handleTogglePublish = async (image: DeviceImageMetadata) => {
    const next = !publishedAssets.has(image.s3Key);
    setPublishingAsset(image.s3Key);
    try {
      await api.patch(`/api/sessions/${sessionId}/assets/publish`, { assetId: image.s3Key, published: next, assetType: 'image' });
      setPublishedAssets(prev => {
        const s = new Set(prev);
        next ? s.add(image.s3Key) : s.delete(image.s3Key);
        return s;
      });
    } catch { /* silent */ } finally {
      setPublishingAsset(null);
    }
  };

  const handleDelete = async (imageId: string) => {
    try {
      await deleteDeviceImage(sessionId, imageId);
      setImages(prev => prev.filter(i => i.imageId !== imageId));
      setSelectedImages(prev => { const next = new Set(prev); next.delete(imageId); return next; });
      setExpandedImage(null);
    } catch (err: any) {
      setError('Delete failed: ' + err.message);
    }
  };

  const handleAddToContext = async (imageId: string) => {
    try {
      await addImageToContext(sessionId, imageId);
      setImages(prev => prev.map(i => i.imageId === imageId ? { ...i, addedToContext: true } : i));
      showToast('Added to session context');
    } catch (err: any) {
      setError('Failed to add to context: ' + err.message);
    }
  };

  const handleAddToGraph = async (imageId: string) => {
    try {
      await addImageToGraph(sessionId, imageId);
      setImages(prev => prev.map(i => i.imageId === imageId ? { ...i, addedToGraph: true } : i));
      showToast('Added to knowledge graph');
    } catch (err: any) {
      setError('Failed to add to graph: ' + err.message);
    }
  };

  const handleToggleTaggedForExport = async (imageId: string, current: boolean) => {
    const next = !current;
    setImages(prev => prev.map(i => i.imageId === imageId ? { ...i, taggedForExport: next } : i));
    try {
      await setImageTaggedForExport(sessionId, imageId, next);
    } catch {
      setImages(prev => prev.map(i => i.imageId === imageId ? { ...i, taggedForExport: current } : i));
    }
  };

  const handleUpdateTags = async (imageId: string, tags: string[]) => {
    // Optimistic update
    setImages(prev => prev.map(i => i.imageId === imageId ? { ...i, tags } : i));
    try {
      await updateImageTags(sessionId, imageId, tags);
    } catch (err: any) {
      fetchImages(); // Revert on failure
      setError('Failed to update tags: ' + err.message);
    }
  };

  const handlePushToScreen = (image: DeviceImageMetadata) => {
    onPinToSecondScreen({
      id: `img-${image.imageId}`,
      type: 'image',
      content: image.presignedUrl,
      title: image.captureLabel || `Image from ${image.deviceId}`,
      description: image.extractedText || undefined,
      source: 'Images',
      pinnedAt: Date.now(),
    });
    showToast('Pushed to second screen');
  };

  const handleAskAvatar = async (question: string, targetRoles?: string[]) => {
    if (!askAvatarImage) return;
    try {
      await askAvatarAboutImage(sessionId, askAvatarImage.imageId, question, targetRoles);
      showToast('Question sent — check Contributions for the response');
    } catch (err: any) {
      setError('Failed to ask avatar: ' + err.message);
    }
  };

  const handleAnnotationSave = async (image: DeviceImageMetadata, annotations: ImageAnnotation[]) => {
    setImages(prev => prev.map(i => i.imageId === image.imageId ? { ...i, annotations } : i));
    try {
      await updateImageAnnotations(sessionId, image.imageId, annotations);
      showToast('Annotations saved');
    } catch (err: any) {
      setError('Failed to save annotations: ' + err.message);
    }
  };

  // ============================================================================
  // BULK ACTIONS
  // ============================================================================

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedImages);
    try {
      await bulkImageAction(sessionId, ids, 'delete');
      setImages(prev => prev.filter(i => !selectedImages.has(i.imageId)));
      setSelectedImages(new Set());
      showToast(`Deleted ${ids.length} images`);
    } catch (err: any) {
      setError('Bulk delete failed: ' + err.message);
    }
  };

  const handleBulkAddToContext = async () => {
    const ids = Array.from(selectedImages);
    try {
      await bulkImageAction(sessionId, ids, 'add-to-context');
      setImages(prev => prev.map(i => selectedImages.has(i.imageId) ? { ...i, addedToContext: true } : i));
      setSelectedImages(new Set());
      showToast(`Added ${ids.length} images to context`);
    } catch (err: any) {
      setError('Bulk add to context failed: ' + err.message);
    }
  };

  const handleBulkAddToGraph = async () => {
    const ids = Array.from(selectedImages);
    try {
      await bulkImageAction(sessionId, ids, 'add-to-graph');
      setImages(prev => prev.map(i => selectedImages.has(i.imageId) ? { ...i, addedToGraph: true } : i));
      setSelectedImages(new Set());
      showToast(`Added ${ids.length} images to graph`);
    } catch (err: any) {
      setError('Bulk add to graph failed: ' + err.message);
    }
  };

  const handleBulkTag = async (tags: string[]) => {
    const ids = Array.from(selectedImages);
    try {
      await bulkImageAction(sessionId, ids, 'update-tags', tags);
      setImages(prev => prev.map(i => selectedImages.has(i.imageId) ? { ...i, tags } : i));
      setSelectedImages(new Set());
      showToast(`Tagged ${ids.length} images`);
    } catch (err: any) {
      setError('Bulk tag failed: ' + err.message);
    }
  };

  const handleBulkPushToScreen = () => {
    const selected = images.filter(i => selectedImages.has(i.imageId));
    selected.forEach(handlePushToScreen);
    setSelectedImages(new Set());
  };

  const downloadImageBlob = async (image: DeviceImageMetadata): Promise<{ blob: Blob; filename: string }> => {
    const resp = await api.get(`/api/device-images/${sessionId}/${image.imageId}/download`, { responseType: 'blob' });
    const blob: Blob = resp.data;
    let ext: string;
    if (blob.type.includes('png')) ext = 'png';
    else if (blob.type.includes('webp')) ext = 'webp';
    else ext = 'jpg';
    const label = (image.captureLabel || image.deviceId || 'image').replace(/[^a-z0-9_-]/gi, '_');
    const ts = new Date(image.timestamp).toISOString().replace(/[:.]/g, '-').slice(0, 19);
    return { blob, filename: `${ts}_${label}.${ext}` };
  };

  const handleDownloadSingle = async (image: DeviceImageMetadata) => {
    try {
      const { blob, filename } = await downloadImageBlob(image);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setError('Download failed');
    }
  };


  // ============================================================================
  // FILTERING & SORTING
  // ============================================================================

  let filteredImages: DeviceImageMetadata[];
  if (sourceFilter === 'all') filteredImages = images;
  else if (sourceFilter === 'manual') filteredImages = images.filter(i => i.captureSource === 'manual');
  else if (sourceFilter === 'ai-generated') filteredImages = images.filter(i => i.captureSource === 'ai-generated');
  else filteredImages = images.filter(i => i.deviceId === sourceFilter);

  const sortedImages = [...filteredImages].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortField === 'date') {
      return dir * (new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }
    if (sortField === 'source') {
      return dir * (a.deviceId || '').localeCompare(b.deviceId || '');
    }
    if (sortField === 'tag') {
      const aTag = (a.tags?.[0] || '').toLowerCase();
      const bTag = (b.tags?.[0] || '').toLowerCase();
      return dir * aTag.localeCompare(bTag);
    }
    return 0;
  });

  // Source options for filter
  const sourceOptions = [
    { value: 'all', label: 'All Sources' },
    ...cameraDevices.map(d => ({
      value: d.deviceId,
      label: d.displayName || d.deviceId
    })),
    { value: 'manual', label: 'Manual Upload' },
    { value: 'ai-generated', label: 'AI Generated' },
  ];

  // Toggle selection
  const toggleSelection = (imageId: string) => {
    setSelectedImages(prev => {
      const next = new Set(prev);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedImages.size === sortedImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(sortedImages.map(i => i.imageId)));
    }
  };

  // Toast helper
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleColumnSort = (field: SortField) => { if (sortField === field) { setSortDir(prev => prev === 'asc' ? 'desc' : 'asc'); } else { setSortField(field); setSortDir('asc'); } };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="font-display flex flex-col h-full">
      {/* ── Header (white bar matching site pattern) ──────────────── */}
      <div className="px-8 py-6 bg-white border-b border-slate-200 shadow-sm shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Camera size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-navy tracking-tight">Images</h2>
              <p className="text-slate-500 text-sm mt-0.5">Capture, manage, and analyze session images</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Capture buttons */}
            {cameraDevices.map(device => {
              const cooldown = captureCooldowns[device.deviceId];
              return (
                <button
                  key={device.deviceId}
                  onClick={() => handleCapture(device.deviceId)}
                  disabled={!!cooldown}
                  title={device.displayName || device.deviceId}
                  className="flex items-center justify-center rounded-xl h-10 px-4 bg-primary text-white text-sm font-bold hover:brightness-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed max-w-[200px]"
                >
                  <Camera size={14} className="mr-2 shrink-0" />
                  <span className="truncate">{device.displayName || device.deviceId}</span>
                  {cooldown ? <span className="ml-1 shrink-0">({cooldown}s)</span> : ''}
                </button>
              );
            })}
            {/* Upload button */}
            <button
              onClick={() => setShowUpload(!showUpload)}
              className="flex items-center justify-center rounded-xl h-10 px-4 border border-slate-200 bg-white text-sm font-bold hover:bg-slate-50 transition-colors"
            >
              <Upload size={14} className="mr-2" /> Upload
            </button>
            {/* Generate Image button */}
            {onGenerateImage && (
              <button
                onClick={() => setShowImageGenModal(true)}
                disabled={imageGenLoading}
                className="flex items-center justify-center rounded-xl h-10 px-4 border border-orange-300 bg-orange-50 text-orange-700 text-sm font-bold hover:bg-orange-100 transition-colors disabled:opacity-50"
              >
                {imageGenLoading ? (
                  <><Loader2 size={14} className="mr-2 animate-spin" />{imageGenCountdown > 0 ? `${imageGenCountdown}s` : 'Generating…'}</>
                ) : (
                  <><ImageIcon size={14} className="mr-2" />Generate Image</>
                )}
              </button>
            )}
            {/* Workshop Book button */}
            {hasWorkshopBookFeature && onGenerateBook && (
              <div className="flex items-center gap-1.5">
                <button
                  onClick={onGenerateBook}
                  className="flex items-center justify-center rounded-xl h-10 px-4 border border-teal-200 bg-teal-50 text-teal-700 text-sm font-bold hover:bg-teal-100 transition-colors"
                  title="Generate a printed A5 workshop book for attendees"
                >
                  <BookOpen size={14} className="mr-2" /> Workshop Book
                </button>
                <WorkshopBookInfoButton />
              </div>
            )}
          </div>
        </div>

        {/* Filter & sort row */}
        <div className="flex items-center gap-4 mt-4">
          {/* Stats */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col bg-white border border-primary/30 rounded-lg px-4 py-2 min-w-[100px] shadow-sm relative overflow-hidden group">
              <div className="absolute inset-0 bg-primary/5 group-hover:bg-primary/10 transition-colors" />
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider z-10">Total</span>
              <span className="text-2xl font-bold text-navy z-10">{images.length}</span>
            </div>
            <div className="flex flex-col bg-white border border-slate-200 rounded-lg px-4 py-2 min-w-[100px] shadow-sm">
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">In Context</span>
              <span className="text-2xl font-bold text-emerald-600">{images.filter(i => i.addedToContext).length}</span>
            </div>
          </div>

          <div className="h-8 w-[1px] bg-slate-200 mx-2" />

          {/* Filter */}
          <select
            value={sourceFilter}
            onChange={e => setSourceFilter(e.target.value)}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-primary/50 hover:text-navy transition-colors cursor-pointer appearance-none pr-8"
          >
            {sourceOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          {/* Sort */}
          <select
            value={`${sortField}-${sortDir}`}
            onChange={e => {
              const [field, dir] = e.target.value.split('-');
              setSortField(field as SortField);
              setSortDir(dir as SortDir);
            }}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:border-primary/50 hover:text-navy transition-colors cursor-pointer appearance-none pr-8"
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="source-asc">Source A-Z</option>
            <option value="source-desc">Source Z-A</option>
            <option value="tag-asc">Tag A-Z</option>
          </select>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-8 mt-4 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center justify-between">
          {error}
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600"><X size={14} /></button>
        </div>
      )}

      {/* Upload form */}
      {showUpload && (
        <div className="mx-8 mt-4 p-4 bg-white rounded-xl border border-primary/20 shadow-sm">
          <div className="flex items-center gap-3">
            <input
              value={uploadLabel}
              onChange={e => setUploadLabel(e.target.value)}
              placeholder="Label (optional)"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-white"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileUpload}
              className="text-sm text-slate-600"
              disabled={uploading}
            />
            {uploading && <span className="text-xs text-slate-500">Uploading...</span>}
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedImages.size > 0 && (
        <div className="mx-8 mt-4 px-4 py-2.5 bg-primary/5 border border-primary/20 rounded-xl flex items-center gap-3">
          <span className="text-sm font-bold text-primary">{selectedImages.size} selected</span>
          <div className="w-px h-5 bg-primary/20" />
          <button onClick={() => setShowBulkTag(true)} className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-primary transition-colors">
            <Tag size={12} /> Tag
          </button>
          <button onClick={handleBulkPushToScreen} className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-primary transition-colors">
            <Monitor size={12} /> Push to Screen
          </button>
          <button onClick={handleBulkAddToContext} className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-primary transition-colors">
            <BookOpen size={12} /> Add to Context
          </button>
          <button onClick={handleBulkAddToGraph} className="flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-teal-600 transition-colors">
            <Share2 size={12} /> Add to Graph
          </button>
          <button onClick={() => setDeleteConfirm({ type: 'bulk' })} className="flex items-center gap-1.5 text-xs font-bold text-red-600 hover:text-red-700 transition-colors ml-auto">
            <Trash2 size={12} /> Delete
          </button>
          <button onClick={() => setSelectedImages(new Set())} className="text-xs text-slate-400 hover:text-slate-600">
            Clear
          </button>
        </div>
      )}

      {/* ── Table Content ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-400 text-sm">Loading images...</div>
        )}
        {!loading && sortedImages.length === 0 && (
          <div className="py-16 text-center bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl">
            <Camera className="mx-auto text-slate-300 mb-4" size={48} />
            <h3 className="text-base font-semibold text-slate-500 mb-1">No Images Yet</h3>
            <p className="text-sm text-slate-400">Capture from a device or upload an image to get started.</p>
          </div>
        )}
        {!loading && sortedImages.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/50">
                  <th className="py-3 px-3 text-left w-8">
                    <input
                      type="checkbox"
                      checked={selectedImages.size === sortedImages.length && sortedImages.length > 0}
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300"
                    />
                  </th>
                  <th className="py-3 px-3 text-left w-16 text-xs font-bold text-slate-500 uppercase tracking-wider">Image</th>
                  <th className="py-3 px-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleColumnSort('source')}>Source<span className='ml-1 inline-flex'><SortIcon active={sortField === 'source'} dir={sortDir} /></span></th>
                  <th className="py-3 px-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleColumnSort('date')}>Time<span className='ml-1 inline-flex'><SortIcon active={sortField === 'date'} dir={sortDir} /></span></th>
                  <th className="py-3 px-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer select-none hover:bg-slate-100 transition-colors" onClick={() => handleColumnSort('tag')}>Tags<span className='ml-1 inline-flex'><SortIcon active={sortField === 'tag'} dir={sortDir} /></span></th>
                  <th className="py-3 px-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="py-3 px-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedImages.map(image => (
                  <tr
                    key={image.imageId}
                    className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                      selectedImages.has(image.imageId) ? 'bg-primary/5' : ''
                    }`}
                  >
                    <td className="py-3 px-3">
                      <input
                        type="checkbox"
                        checked={selectedImages.has(image.imageId)}
                        onChange={() => toggleSelection(image.imageId)}
                        className="rounded border-slate-300"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <img
                        src={image.presignedUrl}
                        alt=""
                        className="w-14 h-10 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-80 hover:shadow-md transition-all"
                        onClick={() => setExpandedImage(image)}
                      />
                    </td>
                    <ImageSourceCell image={image} sessionDevices={sessionDevices} />
                    <td className="py-3 px-3 text-slate-600 text-xs font-medium">
                      {new Date(image.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      <span className="block text-slate-400 font-normal">{new Date(image.timestamp).toLocaleDateString()}</span>
                    </td>
                    <td className="py-3 px-3">
                      <InlineTagEditor
                        tags={image.tags || []}
                        onUpdate={(tags) => handleUpdateTags(image.imageId, tags)}
                      />
                    </td>
                    <ImageStatusCell image={image} />
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setExpandedImage(image)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-navy text-xs font-medium transition-colors"
                        >
                          <ZoomIn size={14} /> View
                        </button>
                        <button
                          onClick={() => setAnnotatingImage(image)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-purple-50 text-slate-500 hover:text-purple-600 text-xs font-medium transition-colors"
                        >
                          <Pencil size={14} /> Annotate
                        </button>
                        {onGenerateImage && (
                          <button
                            onClick={() => { setSourceImage(image); setShowImageGenModal(true); }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-orange-50 text-slate-500 hover:text-orange-600 text-xs font-medium transition-colors"
                          >
                            <ImageIcon size={14} /> Generate
                          </button>
                        )}
                        <button
                          onClick={() => handlePushToScreen(image)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-navy text-xs font-medium transition-colors"
                        >
                          <Monitor size={14} /> Screen
                        </button>
                        <button
                          onClick={() => setAskAvatarImage(image)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-primary/10 text-slate-500 hover:text-primary text-xs font-medium transition-colors"
                        >
                          <MessageSquarePlus size={14} /> Ask
                        </button>
                        {!image.addedToContext && image.extractedText && (
                          <button
                            onClick={() => handleAddToContext(image.imageId)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-navy text-xs font-medium transition-colors"
                          >
                            <BookOpen size={14} /> Context
                          </button>
                        )}
                        {!image.addedToGraph && image.extractedText && (
                          <button
                            onClick={() => handleAddToGraph(image.imageId)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-teal-50 text-slate-500 hover:text-teal-600 text-xs font-medium transition-colors"
                          >
                            <Share2 size={14} /> Graph
                          </button>
                        )}
                        <button
                          onClick={() => handleTogglePublish(image)}
                          disabled={publishingAsset === image.s3Key}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            publishedAssets.has(image.s3Key)
                              ? 'bg-primary/10 text-primary hover:bg-primary/20'
                              : 'hover:bg-slate-100 text-slate-500 hover:text-navy'
                          }`}
                        >
                          {publishedAssets.has(image.s3Key) ? <Eye size={14} /> : <EyeOff size={14} />}
                          {publishedAssets.has(image.s3Key) ? 'Published' : 'Publish'}
                        </button>
                        <button
                          onClick={() => handleToggleTaggedForExport(image.imageId, !!image.taggedForExport)}
                          title={image.taggedForExport ? 'Remove from export' : 'Tag for export'}
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            image.taggedForExport
                              ? 'bg-primary/10 text-primary hover:bg-primary/20'
                              : 'hover:bg-slate-100 text-slate-500 hover:text-navy'
                          }`}
                        >
                          <Bookmark size={14} /> Export
                        </button>
                        <button
                          onClick={() => handleDownloadSingle(image)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-blue-50 text-slate-500 hover:text-blue-600 text-xs font-medium transition-colors"
                        >
                          <Download size={14} /> Download
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ type: 'single', imageId: image.imageId })}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-red-50 text-slate-500 hover:text-red-500 text-xs font-medium transition-colors"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-navy text-white px-4 py-2.5 rounded-xl shadow-lg text-sm font-bold z-50">
          {toast}
        </div>
      )}

      {/* Modals */}
      {expandedImage && (
        <ExpandedImageView
          image={expandedImage}
          onClose={() => setExpandedImage(null)}
          onAnnotate={() => { setAnnotatingImage(expandedImage); setExpandedImage(null); }}
          onPushToScreen={() => { handlePushToScreen(expandedImage); setExpandedImage(null); }}
          onAskAvatar={() => { setAskAvatarImage(expandedImage); setExpandedImage(null); }}
          onAddToContext={() => { handleAddToContext(expandedImage.imageId); setExpandedImage(null); }}
          onAddToGraph={() => { handleAddToGraph(expandedImage.imageId); setExpandedImage(null); }}
          onDelete={() => { setExpandedImage(null); setDeleteConfirm({ type: 'single', imageId: expandedImage.imageId }); }}
          onDownload={() => handleDownloadSingle(expandedImage)}
          onToggleExportTag={() => handleToggleTaggedForExport(expandedImage.imageId, !!expandedImage.taggedForExport)}
          onStartOpenDesignTask={onStartOpenDesignTask}
        />
      )}

      {askAvatarImage && (
        <AskAvatarModal
          image={askAvatarImage}
          avatars={sessionAvatars}
          onSubmit={handleAskAvatar}
          onClose={() => setAskAvatarImage(null)}
        />
      )}

      {annotatingImage && (
        <AnnotationOverlay
          imageUrl={annotatingImage.presignedUrl}
          annotations={annotatingImage.annotations || []}
          onSave={(anns) => handleAnnotationSave(annotatingImage, anns)}
          onClose={() => setAnnotatingImage(null)}
        />
      )}

      {showBulkTag && (
        <BulkTagModal
          count={selectedImages.size}
          onApply={handleBulkTag}
          onClose={() => setShowBulkTag(false)}
        />
      )}

      {showImageGenModal && onGenerateImage && (
        <GenerateImageModal
          onGenerate={handleGenerateImage}
          onClose={() => { if (!imageGenLoading) { setShowImageGenModal(false); setSourceImage(null); } }}
          loading={imageGenLoading}
          countdown={imageGenCountdown}
          taskSourceMode={!!sourceImage}
          taskName={sourceImage?.captureLabel || (sourceImage?.captureSource === 'ai-generated' ? 'AI Generated Image' : undefined)}
          sourceImagePreviewUrl={sourceImage?.presignedUrl}
        />
      )}

      <ConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={async () => {
          if (!deleteConfirm) return;
          if (deleteConfirm.type === 'single') {
            await handleDelete(deleteConfirm.imageId);
          } else {
            await handleBulkDelete();
          }
          setDeleteConfirm(null);
        }}
        title={deleteConfirm?.type === 'bulk' ? `Delete ${selectedImages.size} Images` : 'Delete Image'}
        message={deleteConfirm?.type === 'bulk' ? `Are you sure you want to delete ${selectedImages.size} selected images? This cannot be undone.` : 'Are you sure you want to delete this image? This cannot be undone.'}
        confirmText="Delete"
        variant="warning"
      />
    </div>
  );
};

export default ImagesTab;
